/**
 * Production Preview — resolve what a component row should display.
 *
 * Extracted from the inline JSX in `pages/RMProduction.js` so the
 * display-resolution rules live in one place and can be exercised by
 * a screen-level test without inlining the logic in the assertion.
 *
 * Rules (locked to the ticket's ACs):
 *   AC1  Return `primaryId` + `secondaryLine` as two DISTINCT pieces
 *        of information for the row to render on two separate lines.
 *   AC2  For MB/PWD candidate-driven rows, `secondaryLine` MUST come
 *        from `pickedCand.description` — i.e. the SELECTED candidate's
 *        own `raw_materials.description`, never from the parent INP
 *        component's `description` / `mb` fields.
 *   AC3  Selecting a different candidate ⇒ that candidate's description
 *        (the caller re-computes `pickedCand` and re-invokes us; nothing
 *        stateful lives here).
 *   AC4  If the resolved `secondaryLine` would be identical (ignoring
 *        case + surrounding whitespace) to `primaryId`, return `null`
 *        for `secondaryLine` — the row must show the identifier ONCE,
 *        never as two identical lines.
 *   AC5  When no candidate is picked yet, or the picked candidate has
 *        no `description`, fall back to `comp.pantone_code || comp.name`
 *        — historical behaviour, preserved so non-candidate rows
 *        (INP/POLY/ACC/PWD-without-colourant) render exactly as today.
 *        If the fallback is empty or also equals `primaryId`, return
 *        `null` so no ghost line renders.
 *
 * @param {object} comp        — Preview component row (from `/rm-production/preview`)
 * @param {object|null} pickedCand — The selected MB candidate (or null if none)
 * @param {string} pickedRmId  — The currently-selected `rm_id` (override or default)
 * @returns {{primaryId: string, secondaryLine: string|null}}
 */
export function resolveComponentDisplay(comp, pickedCand, pickedRmId) {
  const primaryId = String(pickedRmId || comp?.rm_id || "");
  const primaryKey = primaryId.trim().toLowerCase();

  // AC1–AC3: prefer the SELECTED candidate's own description. Never
  // read `comp.description` or `comp.mb` — those live on the parent
  // INP record and are stale/missing on 46.5% of live INPs.
  let secondary = String(pickedCand?.description || "").trim();

  // AC5 fallback — historical behaviour for rows without a picked
  // candidate description (non-MB rows, or MB rows where the picked
  // candidate has no description on its raw_materials record).
  if (!secondary) {
    secondary = String((comp?.pantone_code || comp?.name) || "").trim();
  }

  // AC4 dedup: do NOT render a line identical to the identifier.
  if (!secondary || secondary.toLowerCase() === primaryKey) {
    return { primaryId, secondaryLine: null };
  }
  return { primaryId, secondaryLine: secondary };
}
